import { Plane } from 'curtainsjs';
import anime from 'animejs/lib/anime.es';
import vertex from '../shaders/vertex.glsl';
import fragment from '../shaders/fragment.glsl';

export default class {
	// eslint-disable-next-line object-curly-newline
	constructor({ curtains, planeEl, textures, planeParentEl, groupName }) {
		this.params = {
			vertexShader: vertex,
			fragmentShader: fragment,
			widthSegments: 1,
			heightSegments: 1,
			transparent: true,
			uniforms: {
				dotFade: {
					name: 'uDotFade',
					type: '1f',
					value: 1.0,
				},
			},
		};

		this.textures = textures;
		this.planeParentEl = planeParentEl;
		this.planeWrapper = planeParentEl.querySelector('.c-plane__wrapper');
		this.planeImagesParent = planeParentEl.querySelector('.c-plane__images');
		this.planeImages = planeParentEl.querySelectorAll('.c-plane__image');
		this.indicators = planeParentEl.querySelectorAll('[data-plane-indicator]');
		this.indicatorsDuplicate = planeParentEl.querySelectorAll('[data-plane-indicator-duplicate]');
		this.progress = planeParentEl.querySelectorAll('.c-billboard__progress');
		this.details = planeParentEl.querySelectorAll('.c-billboard__details');
		this.hasSlider = planeParentEl.getAttribute('data-autoplay') !== null;
		this.hasTextSlider = planeParentEl.getAttribute('data-text-slider') !== null;
		this.groupName = groupName;
		this.billboardDetailActive = 'c-billboard__details--active';
		this.canvasElement = document.getElementById('canvas');

		this.currentIndex = 0;

		this.plane = new Plane(curtains, planeEl, this.params);
		this.isVideoError = false;

		this.createObserver();
		this.initPlane();
	}

	animateImages() {
		this.planeImages.forEach((image, i) => {
			if (i === this.currentIndex) {
				image.classList.add('active');
			} else {
				image.classList.remove('active');
			}
		});

		this.updateBillboardElements(this.currentIndex);
	}

	animateTextures() {
		const timeline = anime.timeline({
			easing: 'linear',
		});

		this.updateBillboardElements(this.currentIndex);

		timeline
			.add({
				targets: this.plane.uniforms.dotFade,
				value: 0,
				duration: 300,
				complete: () => {
					this.initialTex.setSource(this.textures[`${this.name}-${this.currentIndex + 1}`].source);
					this.textures[`${this.name}-${this.currentIndex + 1}`].source.play();
				},
			})
			.add({
				targets: this.plane.uniforms.dotFade,
				value: 1,
				duration: 300,
			});
	}

	autoPlayTextures() {
		if (this.currentIndex >= 0) {
			this.currentIndex += 1;
		}

		if (this.currentIndex === this.indicators.length) {
			this.currentIndex = 0;
		}

		if (this.isVideoError) {
			this.animateImages();
		} else {
			this.animateTextures();
		}
	}

	updateBillboardElements(index) {
		if (this.progress.length) {
			this.progress.forEach(el => {
				el.setAttribute('style', `--progress: ${(index + 1) / this.indicators.length};`);
			});
		}

		if (this.hasTextSlider) {
			this.details.forEach((element, elIndex) => {
				if (elIndex === index) {
					element.classList.add(this.billboardDetailActive);
				} else {
					element.classList.remove(this.billboardDetailActive);
				}
			});
		}

		if (this.indicators) {
			this.indicators.forEach((item, i) => {
				item.classList.remove('active');

				if (i === this.currentIndex) {
					item.classList.add('active');
				}
			});
		}

		if (this.indicatorsDuplicate) {
			this.indicatorsDuplicate.forEach((item, i) => {
				item.classList.remove('active');

				if (i === this.currentIndex) {
					item.classList.add('active');
				}
			});
		}
	}

	initPlane() {
		this.textures = window.TEXTURES;
		this.name = this.groupName;
		this.initialTex = this.plane.createTexture({
			sampler: 'uTexture',
		});
		this.initialTex.setSource(this.textures[`${this.name}-1`].source);

		if (this.progress.length) {
			this.progress.forEach(el => {
				el.setAttribute('style', `--progress: ${1 / this.indicators.length};`);
			});
		}

		if (this.indicators) {
			this.indicators.forEach((indicator, i) => {
				indicator.addEventListener('click', () => {
					this.currentIndex = i;

					if (this.isVideoError) {
						this.animateImages();
					} else {
						this.animateTextures();
					}

					if (this.hasSlider) {
						clearInterval(this.interval);
						this.interval = setInterval(() => {
							this.autoPlayTextures();
						}, 5000);
					}
				});
			});
		}

		if (this.hasSlider) {
			this.indicators[0].classList.add('active');
		}

		if (this.indicatorsDuplicate.length) {
			this.indicatorsDuplicate[0].classList.add('active');
		}
	}

	enableImages() {
		this.canvasElement.classList.remove('active');
		this.planeImagesParent.classList.add('visible');
		this.plane.remove();

		this.planeImages.forEach(el => {
			const image = el.querySelector('img');
			image.src = image.getAttribute('pre-src');
		});

		this.planeImages[this.currentIndex].classList.add('active');
	}

	createObserver() {
		this.observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					this.textures[`${this.name}-${this.currentIndex + 1}`].source.play().catch(error => {
						if (error.name === 'NotAllowedError') {
							this.isVideoError = true;
							this.enableImages();
						}
					});

					if (this.hasSlider) {
						this.interval = setInterval(() => {
							this.autoPlayTextures();
						}, 5000);
					}

					if (this.indicators.length) {
						this.indicators.forEach(el => {
							el.classList.add('playing');
						});
					}
				} else {
					if (this.hasSlider) {
						clearInterval(this.interval);
					}
					this.textures[`${this.name}-${this.currentIndex + 1}`].source.pause();
					if (this.indicators.length) {
						this.indicators.forEach(el => {
							el.classList.remove('playing');
						});
					}
				}
			});
		});

		this.observer.observe(this.planeParentEl);
	}
}
